/**
 * CSV Import Hooks
 *
 * TanStack Query hooks for CSV import operations with polling and progress tracking.
 * Handles file upload, job status polling, and cancellation.
 */

import { useState, useCallback, useMemo } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  submitCsvImport,
  getImportJobStatus,
  cancelImportJob,
  forceUnstickImportJob,
} from '@/lib/api';
import type { ForceUnstickImportResponse } from '@/lib/api';
import { useAuth } from '@/state/auth-context';
import type {
  SubmitImportResponse,
  ImportJobStatusResponse,
  CancelImportResponse,
  IdempotentImportResponse,
  ImportJobStatus,
} from '@/types/user-migration';

/**
 * Polling interval in milliseconds
 */
const POLLING_INTERVAL_MS = 2000;

/**
 * Terminal job statuses that stop polling
 */
const TERMINAL_STATUSES: ImportJobStatus[] = ['completed', 'failed', 'cancelled'];

/**
 * Check if job status is terminal (polling should stop)
 */
function isTerminalStatus(status: ImportJobStatus): boolean {
  return TERMINAL_STATUSES.includes(status);
}

/**
 * Use Submit CSV Import
 * Mutation hook for uploading a CSV file and starting an import job
 *
 * @returns TanStack Mutation result with upload progress
 */
export function useSubmitCsvImport(): UseMutationResult<
  SubmitImportResponse | IdempotentImportResponse,
  Error,
  { file: File; onUploadProgress?: (progress: number) => void }
> {
  const { getIdToken } = useAuth();

  return useMutation({
    mutationFn: async ({
      file,
      onUploadProgress,
    }: {
      file: File;
      onUploadProgress?: (progress: number) => void;
    }) => {
      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return submitCsvImport(file, token, onUploadProgress);
    },
  });
}

/**
 * Use Import Job Status
 * Query hook for polling job status with automatic refresh
 *
 * @param jobId - UUID of the import job to poll
 * @param enabled - Whether polling should be active
 * @returns TanStack Query result with job status
 */
export function useImportJobStatus(
  jobId: string | null,
  enabled: boolean = true
): UseQueryResult<ImportJobStatusResponse> {
  const { getIdToken } = useAuth();

  const query = useQuery({
    queryKey: ['importJobStatus', jobId],
    queryFn: async ({ signal }) => {
      if (!jobId) {
        throw new Error('No job ID provided');
      }

      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return getImportJobStatus(jobId, token, signal);
    },
    enabled: enabled && jobId !== null,
    refetchInterval: query => {
      // Stop polling if disabled or job is in terminal state
      if (!enabled) return false;
      const data = query.state.data;
      if (data && isTerminalStatus(data.status)) {
        return false;
      }
      // Poll every 2 seconds while active
      return POLLING_INTERVAL_MS;
    },
    refetchOnWindowFocus: false,
    staleTime: 0, // Always fetch fresh data
    gcTime: 60000, // Keep in cache for 1 minute
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 10000),
  });

  return query;
}

/**
 * Use Cancel Import Job
 * Mutation hook for cancelling an in-progress import job
 *
 * @returns TanStack Mutation result
 */
export function useCancelImportJob(): UseMutationResult<CancelImportResponse, Error, string> {
  const { getIdToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (jobId: string) => {
      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return cancelImportJob(jobId, token);
    },
    onSuccess: (_, jobId) => {
      // Invalidate the job status query to trigger a refetch
      queryClient.invalidateQueries({ queryKey: ['importJobStatus', jobId] });
    },
  });
}

/**
 * Use Force Unstick Import Job
 * Mutation hook for force-unsticking a stuck import job
 *
 * @returns TanStack Mutation result
 */
export function useForceUnstickImportJob(): UseMutationResult<
  ForceUnstickImportResponse,
  Error,
  { jobId: string; reason?: string }
> {
  const { getIdToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ jobId, reason }: { jobId: string; reason?: string }) => {
      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return forceUnstickImportJob(jobId, token, reason);
    },
    onSuccess: (_, { jobId }) => {
      // Invalidate the job status query to trigger a refetch
      queryClient.invalidateQueries({ queryKey: ['importJobStatus', jobId] });
    },
  });
}

/**
 * CSV Import State Machine States
 */
export type CsvImportState =
  | 'CLOSED'
  | 'FILE_SELECTION'
  | 'FILE_SELECTED'
  | 'FILE_ERROR'
  | 'UPLOADING'
  | 'UPLOAD_ERROR'
  | 'PROCESSING'
  | 'PROCESSING_ERROR'
  | 'COMPLETED'
  | 'CANCELLED';

/**
 * CSV Import State Machine Hook
 * Manages the state machine for the CSV import dialog flow
 */
export function useCsvImportStateMachine() {
  const [internalState, setInternalState] = useState<CsvImportState>('CLOSED');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [jobId, setJobId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isIdempotent, setIsIdempotent] = useState(false);

  const queryClient = useQueryClient();

  // Hooks
  const submitMutation = useSubmitCsvImport();
  const cancelMutation = useCancelImportJob();
  const forceUnstickMutation = useForceUnstickImportJob();
  const jobStatusQuery = useImportJobStatus(jobId, internalState === 'PROCESSING');

  // Derive state from query data to avoid setState in effects
  const state = useMemo((): CsvImportState => {
    // Only override state based on job status when we're in PROCESSING state
    if (internalState === 'PROCESSING' && jobStatusQuery.data) {
      const status = jobStatusQuery.data.status;
      if (status === 'completed') return 'COMPLETED';
      if (status === 'failed') return 'PROCESSING_ERROR';
      if (status === 'cancelled') return 'CANCELLED';
    }

    // Handle polling errors while processing
    if (internalState === 'PROCESSING' && jobStatusQuery.error) {
      return 'PROCESSING_ERROR';
    }

    return internalState;
  }, [internalState, jobStatusQuery.data, jobStatusQuery.error]);

  // Derive error message from query data
  const derivedError = useMemo((): string | null => {
    const queryError = jobStatusQuery.error;
    const queryData = jobStatusQuery.data;

    if (state === 'PROCESSING_ERROR') {
      if (queryError) {
        return queryError.message;
      }
      if (queryData?.errorMessage) {
        return queryData.errorMessage;
      }
      return error || 'Import failed';
    }
    return error;
  }, [state, jobStatusQuery.error, jobStatusQuery.data, error]);

  // Actions
  const open = useCallback(() => {
    setInternalState('FILE_SELECTION');
    setSelectedFile(null);
    setUploadProgress(0);
    setJobId(null);
    setError(null);
    setIsIdempotent(false);
  }, []);

  const close = useCallback(() => {
    setInternalState('CLOSED');
    setSelectedFile(null);
    setUploadProgress(0);
    setJobId(null);
    setError(null);
    setIsIdempotent(false);

    // Invalidate migration users query to refresh the table if an import was completed
    queryClient.invalidateQueries({ queryKey: ['migrationUsers'] });
  }, [queryClient]);

  const selectFile = useCallback((file: File) => {
    setSelectedFile(file);
    setError(null);
    setInternalState('FILE_SELECTED');
  }, []);

  const setFileError = useCallback((errorMessage: string) => {
    setError(errorMessage);
    setInternalState('FILE_ERROR');
  }, []);

  const clearFileSelection = useCallback(() => {
    setSelectedFile(null);
    setError(null);
    setInternalState('FILE_SELECTION');
  }, []);

  const startUpload = useCallback(async () => {
    if (!selectedFile) return;

    setInternalState('UPLOADING');
    setUploadProgress(0);
    setError(null);

    try {
      const result = await submitMutation.mutateAsync({
        file: selectedFile,
        onUploadProgress: setUploadProgress,
      });

      // Check if this was an idempotent response (file already imported)
      if ('createdAt' in result) {
        setIsIdempotent(true);
        setJobId(result.jobId);

        // If the existing job is terminal, show the result
        if (isTerminalStatus(result.status)) {
          if (result.status === 'completed') {
            setInternalState('COMPLETED');
          } else if (result.status === 'failed') {
            setInternalState('PROCESSING_ERROR');
            setError('This file was previously imported and failed');
          } else if (result.status === 'cancelled') {
            setInternalState('CANCELLED');
          }
        } else {
          // Job is still in progress, start polling
          setInternalState('PROCESSING');
        }
      } else {
        // New job started
        setJobId(result.jobId);
        setInternalState('PROCESSING');
      }
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : 'Upload failed');
      setInternalState('UPLOAD_ERROR');
    }
  }, [selectedFile, submitMutation]);

  const retryUpload = useCallback(() => {
    if (selectedFile) {
      startUpload();
    } else {
      setInternalState('FILE_SELECTION');
    }
  }, [selectedFile, startUpload]);

  const retryPolling = useCallback(() => {
    if (jobId) {
      setError(null);
      setInternalState('PROCESSING');
      queryClient.invalidateQueries({ queryKey: ['importJobStatus', jobId] });
    }
  }, [jobId, queryClient]);

  const cancelJob = useCallback(async () => {
    if (!jobId) return;

    try {
      await cancelMutation.mutateAsync(jobId);
      setInternalState('CANCELLED');
    } catch {
      // Even if cancel fails, try to update state based on actual job status
      queryClient.invalidateQueries({ queryKey: ['importJobStatus', jobId] });
    }
  }, [jobId, cancelMutation, queryClient]);

  const forceUnstickJob = useCallback(
    async (reason?: string) => {
      if (!jobId) return;

      try {
        await forceUnstickMutation.mutateAsync({ jobId, reason });
        setInternalState('PROCESSING_ERROR');
        setError('Job was force-unstuck');
      } catch (unstickError) {
        // Even if force-unstick fails, try to update state based on actual job status
        queryClient.invalidateQueries({ queryKey: ['importJobStatus', jobId] });
        throw unstickError;
      }
    },
    [jobId, forceUnstickMutation, queryClient]
  );

  return {
    // State
    state,
    selectedFile,
    uploadProgress,
    jobId,
    error: derivedError,
    isIdempotent,
    jobStatus: jobStatusQuery.data,
    isJobStatusLoading: jobStatusQuery.isLoading,
    isCancelling: cancelMutation.isPending,
    isForceUnsticking: forceUnstickMutation.isPending,
    isUploading: submitMutation.isPending,

    // Actions
    open,
    close,
    selectFile,
    setFileError,
    clearFileSelection,
    startUpload,
    retryUpload,
    retryPolling,
    cancelJob,
    forceUnstickJob,
  };
}
